/**
 * AI创作中心 - 模型配置
 * 模型列表通过接口 getavailableaimodelgroup 动态加载（按 type 分组：1-生图 2-生视频 3-会话 4-文件处理）
 * 本文件维护模块级模型列表存储、参数格式、分辨率选项等工具函数
 */
import { getAvailableAiModelGroup } from '@/_serveice/aiCreation';

// ==================== 模型列表（接口动态加载，加载前为空） ====================

// 生图模型（type = 1）
export let imageModels = [];

// 会话模型（type = 3）
export let chatModels = [];

// 生视频模型（type = 2）
export let videoModels = [];

// 文件处理模型（type = 4）
export let fileModels = [];

/**
 * 从接口加载可用模型分组，并替换模块内各模型列表
 * 接口返回 data 结构：[{ type, name, models: [{ code, name, config }] }]，config 即模型完整配置
 * type 取值：1-生图 2-生视频 3-会话 4-文件处理
 * @returns {Object} { imageModels, videoModels, chatModels, fileModels }
 * @throws 接口失败时抛错（保持原列表不变）
 */
export async function loadAvailableModelGroups() {
  const res = await getAvailableAiModelGroup();
  if (!res?.success || !Array.isArray(res.data)) {
    throw new Error(res?.errorMessage || '获取可用模型失败');
  }
  const groupMap = { 1: [], 2: [], 3: [], 4: [] };
  res.data.forEach((group) => {
    const list = groupMap[group?.type];
    if (!list || !Array.isArray(group.models)) return;
    group.models.forEach((m) => {
      if (m?.config) {
        list.push(m.config);
      }
    });
  });
  imageModels = groupMap[1];
  videoModels = groupMap[2];
  chatModels = groupMap[3];
  fileModels = groupMap[4];
  return { imageModels, videoModels, chatModels, fileModels };
}

/**
 * 校验自定义宽高是否符合模型限制
 * 文档中的范围（如 768*768 到 2048*2048）指的是宽*高乘积的范围
 * @param {Object} model - 生图模型配置
 * @param {number} width - 宽
 * @param {number} height - 高
 * @returns {{ valid: boolean, message?: string }}
 */
export function validateImageCustomSize(model, width, height) {
  if (!model) {
    return { valid: false, message: '未找到模型配置' };
  }
  const w = Number(width);
  const h = Number(height);
  if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
    return { valid: false, message: '请输入有效的宽高' };
  }

  // 使用乘积范围校验
  if (model.productRange && model.productRange.length === 2) {
    const [minProduct, maxProduct] = model.productRange;
    const product = w * h;
    if (product < minProduct || product > maxProduct) {
      return {
        valid: false,
        message: `宽*高乘积需在 ${minProduct} ~ ${maxProduct} 之间（当前 ${product}）`,
      };
    }
  }

  return { valid: true };
}

// 生图分辨率选项
export const imageSizeOptions = [
  { label: '自动', value: 'auto' },
  { label: '1K', value: '1K' },
  { label: '2K', value: '2K' },
  { label: '3K', value: '3K' },
  { label: '4K', value: '4K' },
];

// 尺寸对应的基准像素（以短边为基准）
const sizeBasePixels = {
  '1K': 1024,
  '2K': 2048,
  '3K': 3072,
  '4K': 4096,
};

// 所有支持的比例
export const allRatios = [
  '21:9',
  '16:9',
  '3:2',
  '4:3',
  '1:1',
  '3:4',
  '2:3',
  '9:16',
];

// 比例对应的宽高比数值
const ratioMap = {
  '21:9': 21 / 9,
  '16:9': 16 / 9,
  '3:2': 3 / 2,
  '4:3': 4 / 3,
  '1:1': 1,
  '3:4': 3 / 4,
  '2:3': 2 / 3,
  '9:16': 9 / 16,
};

/**
 * 根据尺寸和比例计算宽高
 * @param {string} size - 尺寸 (1K/2K/3K/4K)
 * @param {string} ratio - 比例
 * @param {number} multiple - 宽高需对齐的倍数（如 16），默认 1 表示不限制
 * @returns {{ width: number, height: number }}
 */
export function sizeRatioToWidthHeight(size, ratio, multiple = 1) {
  const base = sizeBasePixels[size] || 2048;
  const ratioValue = ratioMap[ratio] || 1;

  let width, height;
  if (ratioValue >= 1) {
    // 横屏或方形：短边 = base
    height = base;
    width = Math.round(base * ratioValue);
  } else {
    // 竖屏：短边 = base
    width = base;
    height = Math.round(base / ratioValue);
  }

  // 若配置了倍数（如 gpt 系列要求 16 的倍数），将宽高向下微调为 multiple 的倍数
  if (multiple > 1) {
    width = Math.floor(width / multiple) * multiple;
    height = Math.floor(height / multiple) * multiple;
  }

  return { width, height };
}

/**
 * 根据模型配置过滤可用的 size 和 ratio 组合
 * @param {Object} model - 生图模型配置
 * @returns {{ availableSizes: string[], availableRatios: string[] }}
 */
export function getAvailableSizeAndRatios(model) {
  if (!model) {
    return { availableSizes: ['1K', '2K', '4K'], availableRatios: allRatios };
  }

  // 获取模型支持的 size
  const modelSizes = model.sizes || ['1K', '2K', '4K'];

  // 获取模型的乘积范围限制
  const productRange = model.productRange;
  if (!productRange || productRange.length !== 2) {
    return { availableSizes: modelSizes, availableRatios: allRatios };
  }

  const [minProduct, maxProduct] = productRange;

  // 过滤出有效的 size + ratio 组合
  const validSizes = new Set();
  const validRatios = new Set();

  for (const size of modelSizes) {
    for (const ratio of allRatios) {
      const { width, height } = sizeRatioToWidthHeight(
        size,
        ratio,
        model.multiple || 1,
      );
      const product = width * height;

      // 检查乘积是否在范围内
      if (product >= minProduct && product <= maxProduct) {
        validSizes.add(size);
        validRatios.add(ratio);
      }
    }
  }

  return {
    availableSizes: Array.from(validSizes),
    availableRatios: Array.from(validRatios),
  };
}

/**
 * 根据选择的 size 过滤可用的 ratio（动态联动）
 * @param {Object} model - 生图模型配置
 * @param {string} selectedSize - 当前选择的 size
 * @returns {string[]} 可用的 ratio 列表
 */
export function getAvailableRatiosForSize(model, selectedSize) {
  if (!model || !selectedSize) {
    return allRatios;
  }

  const productRange = model.productRange;
  if (!productRange || productRange.length !== 2) {
    return allRatios;
  }

  const [minProduct, maxProduct] = productRange;
  const validRatios = [];

  for (const ratio of allRatios) {
    const { width, height } = sizeRatioToWidthHeight(
      selectedSize,
      ratio,
      model.multiple || 1,
    );
    const product = width * height;

    if (product >= minProduct && product <= maxProduct) {
      validRatios.push(ratio);
    }
  }

  return validRatios;
}

/**
 * 根据选择的 ratio 过滤可用的 size（动态联动）
 * @param {Object} model - 生图模型配置
 * @param {string} selectedRatio - 当前选择的 ratio
 * @returns {string[]} 可用的 size 列表
 */
export function getAvailableSizesForRatio(model, selectedRatio) {
  if (!model || !selectedRatio) {
    return ['1K', '2K', '4K'];
  }

  const modelSizes = model.sizes || ['1K', '2K', '4K'];
  const productRange = model.productRange;
  if (!productRange || productRange.length !== 2) {
    return modelSizes;
  }

  const [minProduct, maxProduct] = productRange;
  const validSizes = [];

  for (const size of modelSizes) {
    const { width, height } = sizeRatioToWidthHeight(
      size,
      selectedRatio,
      model.multiple || 1,
    );
    const product = width * height;

    if (product >= minProduct && product <= maxProduct) {
      validSizes.push(size);
    }
  }

  return validSizes;
}

// 生图数量选项
export const imageQuantityOptions = [
  { label: '1张', value: 1 },
  { label: '2张', value: 2 },
  { label: '3张', value: 3 },
  { label: '4张', value: 4 },
];

// 视频分辨率选项
export const videoResolutionOptions = [
  { label: '480p', value: '480p' },
  { label: '720p', value: '720p' },
  { label: '1080p', value: '1080p' },
  { label: '4k', value: '4k' },
];

// ==================== 工具函数 ====================

/**
 * 构建生图模型的 promptSetting (JSON字符串)
 * @param {Object} params - 用户输入参数
 * @param {string} params.modelCode - 模型代码
 * @param {string} params.prompt - 提示词
 * @param {number} params.number - 数量
 * @param {string} params.size - 分辨率 (1K/2K/3K/4K)，与 width/height 互斥
 * @param {number} params.width - 宽度
 * @param {number} params.height - 高度
 * @param {string[]} params.images - 参考图URL数组
 * @param {Object} params.setting - 特殊配置 (gpt-5.5 需要)
 * @returns {string} JSON字符串
 */
export function buildImagePromptSetting(params) {
  const { modelCode, prompt, number, size, width, height, images, setting } =
    params;
  const payload = {
    modelCode,
    prompt,
    number,
  };

  // width/height 优先（由比例+尺寸计算得出），没有则用 size
  if (width && height) {
    payload.width = width;
    payload.height = height;
  } else if (size) {
    payload.size = size;
  }

  if (images && images.length > 0) {
    payload.images = images;
  }

  if (setting) {
    payload.setting = setting;
  }

  return JSON.stringify(payload);
}

/**
 * 构建聊天对话模型的 promptSetting (JSON字符串)
 * @param {Object} params - 用户输入参数
 * @param {string} params.modelCode - 模型代码
 * @param {string} params.prompt - 提示词
 * @param {string[]} params.attachments - 上传的图片URL数组
 * @returns {string} JSON字符串
 */
export function buildChatPromptSetting(params) {
  const { modelCode, prompt, attachments } = params;
  const payload = {
    modelCode,
    prompt,
  };
  if (attachments && attachments.length > 0) {
    payload.attachments = attachments;
  }
  return JSON.stringify(payload);
}

/**
 * 构建生视频模型的 promptSetting (JSON字符串)
 * @param {Object} params - 用户输入参数
 * @param {string} params.modelCode - 模型代码
 * @param {string} params.prompt - 提示词
 * @param {string} params.ratio - 比例
 * @param {string} params.duration - 时长(秒)
 * @param {string} params.resolution - 分辨率
 * @param {string} params.firstFrameImage - 首帧图片URL
 * @param {string} params.lastFrameImage - 尾帧图片URL
 * @param {string[]} params.referenceVideos - 参考视频URL数组
 * @param {string[]} params.referenceImages - 参考图片URL数组
 * @param {string[]} params.referenceAudios - 参考音频URL数组
 * @param {Object} params.setting - 特殊配置
 * @returns {string} JSON字符串
 */
export function buildVideoPromptSetting(params) {
  const {
    modelCode,
    prompt,
    ratio,
    duration,
    resolution,
    firstFrameImage,
    lastFrameImage,
    referenceVideos,
    referenceImages,
    referenceAudios,
    setting,
  } = params;

  const payload = {
    modelCode,
    prompt,
    ratio,
    duration,
    resolution,
  };

  if (firstFrameImage) {
    payload.firstFrameImage = firstFrameImage;
  }
  if (lastFrameImage) {
    payload.lastFrameImage = lastFrameImage;
  }
  if (referenceVideos && referenceVideos.length > 0) {
    payload.referenceVideos = referenceVideos;
  }
  if (referenceImages && referenceImages.length > 0) {
    payload.referenceImages = referenceImages;
  }
  if (referenceAudios && referenceAudios.length > 0) {
    payload.referenceAudios = referenceAudios;
  }
  if (setting) {
    payload.setting = setting;
  }

  return JSON.stringify(payload);
}

/**
 * 根据时长范围生成时长选项
 * @param {number[]} durationRange - 时长范围 [min, max]
 * @returns {Array} 时长选项数组
 */
export function generateDurationOptions(durationRange) {
  if (!durationRange || durationRange.length !== 2) {
    return [
      { label: '4s', value: '4' },
      { label: '5s', value: '5' },
      { label: '6s', value: '6' },
      { label: '7s', value: '7' },
      { label: '8s', value: '8' },
      { label: '9s', value: '9' },
      { label: '10s', value: '10' },
      { label: '11s', value: '11' },
      { label: '12s', value: '12' },
      { label: '13s', value: '13' },
      { label: '14s', value: '14' },
      { label: '15s', value: '15' },
    ];
  }
  const [min, max] = durationRange;
  const options = [];
  for (let i = min; i <= max; i++) {
    options.push({ label: `${i}s`, value: String(i) });
  }
  return options;
}

// ==================== 文件处理模型 ====================

// 支持上传的文件扩展名（后缀小写）
export const fileAcceptExtensions = [
  '.pdf',
  '.txt',
  '.md',
  '.json',
  '.html',
  '.xml',
  '.doc',
  '.docx',
  '.rtf',
  '.odt',
  '.ppt',
  '.pptx',
  '.csv',
  '.xls',
  '.xlsx',
];

/**
 * 根据模型代码查找文件处理模型配置
 */
export function findFileModelByCode(code) {
  return fileModels.find((m) => m.value === code);
}

/**
 * 构建文件处理模型的 promptSetting (JSON字符串)
 * 参数固定三个：modelCode、prompt、fileUrl
 * @param {Object} params - 用户输入参数
 * @param {string} params.modelCode - 模型代码
 * @param {string} params.prompt - 提示词
 * @param {string} params.fileUrl - 上传文件的地址
 * @returns {string} JSON字符串
 */
export function buildFilePromptSetting(params) {
  const { modelCode, prompt, fileUrl } = params;
  const payload = { modelCode, prompt, fileUrl };
  return JSON.stringify(payload);
}

/**
 * 根据模型代码查找模型配置
 */
export function findImageModelByCode(code) {
  return imageModels.find((m) => m.value === code);
}

/**
 * 是否为上下文对话型生图模型（gpt-5.5 / gpt-6-astra）
 * 这类模型支持图文混合对话，返回文本按聊天组件渲染
 */
export function isChatLikeModel(code) {
  return !!imageModels.find((m) => m.value === code)?.isChatLike;
}

export function findVideoModelByCode(code) {
  return videoModels.find((m) => m.value === code);
}

export function findChatModelByCode(code) {
  return chatModels.find((m) => m.value === code);
}
